import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LayoutGrid, Rows, Info, ChevronDown } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { useVipSource } from '../../context/VipSourceContext';

// ─── formatters ──────────────────────────────────────────────────────────────

const num = (v) => parseFloat(v) || 0;
const fmtEur = (n) => { if (n == null) return '—'; const a = Math.abs(n); if (a >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M€'; if (a >= 1_000) return Math.round(n / 1_000) + 'K€'; return Math.round(n) + '€'; };
const fmtPct = (v) => (v == null ? '—' : v.toFixed(1) + '%');
const fmtTime = (s) => { const m = Math.round((s || 0) / 60); if (m > 60) return `${Math.floor(m / 60)}h ${m % 60}m`; return m > 0 ? `${m}m` : '—'; };
const colorEur = (v, pos = '#00BD71') => ({ color: v >= 0 ? pos : '#E21351' });

// ─── enrichment ──────────────────────────────────────────────────────────────

function playerInitials(p) {
  const name = p.username || p.email || '';
  const parts = name.split(/[\s@._-]/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '')).toUpperCase();
}

function daysUntilBirthday(dob) {
  if (!dob) return null;
  const d = new Date(dob); if (isNaN(d)) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  let b = new Date(now.getFullYear(), d.getMonth(), d.getDate());
  if (b < now) b = new Date(now.getFullYear() + 1, d.getMonth(), d.getDate());
  return Math.round((b - now) / 86400000);
}

function enrichPlayer(p) {
  const d = (f) => num(p[f]);
  const dep1 = d('dep_1d'), dep7 = d('dep_7d'), dep30 = d('dep_30d');
  const cash1 = d('cash_1d'), cash7 = d('cash_7d'), cash30 = d('cash_30d');
  const ggr1 = d('ggr_1d'), ggr7 = d('ggr_7d'), ggr30 = d('ggr_30d');
  const ngr1 = d('ngr_1d'), ngr7 = d('ngr_7d'), ngr30 = d('ngr_30d');
  const turn1 = d('turnover_1d'), turn7 = d('turnover_7d'), turn30 = d('turnover_30d');
  const bonus1 = d('bonus_1d'), bonus7 = d('bonus_7d');
  const bonusEur7 = d('bonus_eur_7d'), bonusEur30 = d('bonus_eur_30d');
  const play1 = d('playtime_1d_sec'), play7 = d('playtime_7d_sec'), play30 = d('playtime_30d_sec');
  const bets1 = d('n_bets_1d'), bets7 = d('n_bets_7d'), bets30 = d('n_bets_30d');
  const fee7 = d('game_fee_7d');
  const lifeNgr = d('life_ngr');
  const weeklyNgr = ['w1_ngr','w2_ngr','w3_ngr','w4_ngr','w5_ngr','w6_ngr'].map(k => p[k] == null ? null : parseFloat(p[k]));
  const wl6 = weeklyNgr.map(h => h === null ? null : h < 0);
  const avgWeekly = turn30 / 4.28;
  const recentMult = avgWeekly > 0 ? turn7 / avgWeekly : (turn7 > 0 ? 2 : 0);
  const trajectory = turn30 === 0 ? 'inactive'
    : recentMult > 1.2  ? 'up'
    : recentMult < 0.8  ? 'down'
    : 'flat';
  const bonusPct = turn7 > 0 ? (bonus7 / turn7) * 100 : 0;
  const bonusAccel = bonusEur7 / 7 - bonusEur30 / 30; // negative = accelerating (field is negative cost)
  const ngrChange  = ngr7 / 7 - ngr30 / 30;
  const comp_signal = bonusAccel < 0 && ngrChange < 0 ? 'burning'
    : bonusAccel < 0 && ngrChange >= 0 ? 'efficient'
    : ngrChange  < 0 ? 'ngr_down'
    : 'ok';
  return {
    ...p,
    dep_1d: dep1, dep_7d: dep7, dep_30d: dep30,
    cash_1d: cash1, cash_7d: cash7, cash_30d: cash30,
    ggr_1d: ggr1, ggr_7d: ggr7, ggr_30d: ggr30,
    ngr_1d: ngr1, ngr_7d: ngr7, ngr_30d: ngr30, ngr_6m: d('ngr_6m'), life_ngr: lifeNgr,
    turnover_1d: turn1, turnover_7d: turn7, turnover_30d: turn30,
    bonus_1d: bonus1, bonus_7d: bonus7, bonus_30d: d('bonus_30d'),
    n_bets_1d: bets1, n_bets_7d: bets7, n_bets_30d: bets30,
    playtime_1d_sec: play1, playtime_7d_sec: play7, playtime_30d_sec: play30,
    top_game_ggr: d('top_game_ggr'), worst_game_ggr: d('worst_game_ggr'),
    weeklyNgr, wl6,
    avg_bet_size_1d: bets1 > 0 ? turn1 / bets1 : null,
    avg_bet_size_7d: bets7 > 0 ? turn7 / bets7 : null,
    avg_bet_size_30d: bets30 > 0 ? turn30 / bets30 : null,
    house_edge_pct_1d: turn1 > 0 ? ggr1 / turn1 * 100 : null,
    house_edge_pct_7d: turn7 > 0 ? ggr7 / turn7 * 100 : null,
    house_edge_pct_30d: turn30 > 0 ? ggr30 / turn30 * 100 : null,
    ngr_dep_ratio_1d: dep1 > 0 ? ngr1 / dep1 * 100 : null,
    ngr_dep_ratio_7d: dep7 > 0 ? ngr7 / dep7 * 100 : null,
    ngr_dep_ratio_30d: dep30 > 0 ? ngr30 / dep30 * 100 : null,
    bonus_ggr_ratio_1d: ggr1 > 0 ? bonus1 / ggr1 * 100 : null,
    bonus_ggr_ratio_7d: ggr7 > 0 ? bonus7 / ggr7 * 100 : null,
    game_fee_pct_7d: turn7 > 0 ? fee7 / turn7 * 100 : null,
    ngr_per_hour_7d: play7 > 0 ? ngr7 / (play7 / 3600) : null,
    hold_7d: d('hold_7d'),
    recentMult, trajectory, bonusPct, comp_signal,
    playtime: fmtTime(play7),
    initials: playerInitials(p),
    days_until_birthday: daysUntilBirthday(p.date_of_birth),
    poba:       p.prob_of_being_alive         != null ? parseFloat(p.prob_of_being_alive)         : null,
    poba7avg:   p.prob_of_being_alive_7d_avg  != null ? parseFloat(p.prob_of_being_alive_7d_avg)  : null,
    poba7min:   p.prob_of_being_alive_7d_min  != null ? parseFloat(p.prob_of_being_alive_7d_min)  : null,
    poba7max:   p.prob_of_being_alive_7d_max  != null ? parseFloat(p.prob_of_being_alive_7d_max)  : null,
    poba30avg:  p.prob_of_being_alive_30d_avg != null ? parseFloat(p.prob_of_being_alive_30d_avg) : null,
    poba30min:  p.prob_of_being_alive_30d_min != null ? parseFloat(p.prob_of_being_alive_30d_min) : null,
    poba30max:  p.prob_of_being_alive_30d_max != null ? parseFloat(p.prob_of_being_alive_30d_max) : null,
    cltv:       p.cltv_label                  != null ? parseInt(p.cltv_label)                    : null,
    cltv7avg:   p.cltv_label_7d_avg           != null ? parseFloat(p.cltv_label_7d_avg)           : null,
    cltv30avg:  p.cltv_label_30d_avg          != null ? parseFloat(p.cltv_label_30d_avg)          : null,
  };
}

// ─── filter / sort ────────────────────────────────────────────────────────────

function applyFilter(players, filter, pobaDelta = 0.03) {
  // PoBA health (current value)
  if (filter === 'poba_healthy')  return players.filter(p => p.poba != null && p.poba >= 0.7);
  if (filter === 'poba_at_risk')  return players.filter(p => p.poba != null && p.poba >= 0.4 && p.poba < 0.7);
  if (filter === 'poba_alarming') return players.filter(p => p.poba != null && p.poba < 0.4);
  // PoBA trend: compare 7d min vs 30d avg
  if (filter === 'poba_down') return players.filter(p =>
    p.poba7min != null && p.poba30avg != null && p.poba30avg - p.poba7min >= pobaDelta);
  if (filter === 'poba_up')   return players.filter(p =>
    p.poba7avg != null && p.poba30avg != null && p.poba7avg - p.poba30avg >= pobaDelta);

  const affTitle  = (p) => (p.aff_title || '').toLowerCase();
  const isAffZero = (p) => parseFloat(p.aff_fee_lifetime) === 0 || affTitle(p).includes('zero') || affTitle(p).includes('empty');
  const affStrat  = (p) => (p.aff_strategy || '').toLowerCase();
  if (filter === 'aff_rev')  return players.filter(p => p.aff_strategy && affStrat(p).includes('cpa') === false && !isAffZero(p));
  if (filter === 'aff_zero') return players.filter(p => p.aff_strategy && isAffZero(p));
  if (filter === 'aff_cpa')  return players.filter(p => p.aff_strategy && affStrat(p).includes('cpa'));
  if (filter === 'aff_none') return players.filter(p => !p.aff_strategy);
if (filter === 'comp_burning')   return players.filter(p => p.comp_signal === 'burning');
  if (filter === 'comp_efficient') return players.filter(p => p.comp_signal === 'efficient');
  if (filter === 'comp_ngr_down')  return players.filter(p => p.comp_signal === 'ngr_down');
  if (filter === 'comp_ok')        return players.filter(p => p.comp_signal === 'ok');
  return players;
}

function applySort(players, sort, dir) {
  return [...players].sort((a, b) =>
    dir === 'asc' ? (a[sort] ?? 0) - (b[sort] ?? 0) : (b[sort] ?? 0) - (a[sort] ?? 0)
  );
}

// ─── badges ───────────────────────────────────────────────────────────────────

const BADGE_STYLES = {
  warning:  { bg: 'rgba(255,210,47,0.15)',  color: '#FFD22F' },
  negative: { bg: 'rgba(226,19,81,0.15)',   color: '#E21351' },
  positive: { bg: 'rgba(0,189,113,0.15)',   color: '#00BD71' },
  muted:    { bg: 'rgba(154,160,173,0.15)', color: '#9aa0ad' },
  info:     { bg: 'rgba(79,195,247,0.15)',  color: '#4fc3f7' },
};

function getBadges(p, pobaDelta = 0.03) {
  const b = [];

  // PoBA health (current value)
  if (p.poba != null) {
    if (p.poba >= 0.7)
      b.push({ label: 'PoBA healthy',  variant: 'positive', tooltip: `PoBA ${(p.poba * 100).toFixed(0)}% — probability of being alive is high. Player is likely still active.` });
    else if (p.poba >= 0.4)
      b.push({ label: 'PoBA at risk',  variant: 'warning',  tooltip: `PoBA ${(p.poba * 100).toFixed(0)}% — early churn signals. Consider a targeted retention offer.` });
    else
      b.push({ label: 'PoBA alarming', variant: 'negative', tooltip: `PoBA ${(p.poba * 100).toFixed(0)}% — high churn risk. Immediate action recommended.` });
  }

  // PoBA trend: 7d min vs 30d avg
  if (p.poba7min != null && p.poba30avg != null && p.poba30avg - p.poba7min >= pobaDelta)
    b.push({ label: 'PoBA down', variant: 'negative', tooltip: `7d min PoBA (${(p.poba7min * 100).toFixed(0)}%) is ${((p.poba30avg - p.poba7min) * 100).toFixed(1)}pp below 30d avg (${(p.poba30avg * 100).toFixed(0)}%) — retention is deteriorating.` });
  else if (p.poba7avg != null && p.poba30avg != null && p.poba7avg - p.poba30avg >= pobaDelta)
    b.push({ label: 'PoBA up',   variant: 'positive', tooltip: `7d avg PoBA (${(p.poba7avg * 100).toFixed(0)}%) is ${((p.poba7avg - p.poba30avg) * 100).toFixed(1)}pp above 30d avg (${(p.poba30avg * 100).toFixed(0)}%) — retention is improving.` });

  if (p.comp_signal === 'burning')
    b.push({ label: 'Burning',   variant: 'negative', tooltip: 'Bonus spend accelerating while NGR is declining — bonuses are not converting to revenue.' });
  if (p.comp_signal === 'efficient')
    b.push({ label: 'Efficient', variant: 'positive', tooltip: 'Bonus spend up and NGR also improving — bonuses are converting to revenue.' });
  if (p.comp_signal === 'ngr_down')
    b.push({ label: 'NGR down',  variant: 'warning',  tooltip: 'NGR is declining despite stable bonus spend — player may be disengaging.' });

  // Affiliate badge
  if (p.aff_strategy) {
    const strat      = (p.aff_strategy || '').toLowerCase();
    const titleLower = (p.aff_title    || '').toLowerCase();
    const isZero     = titleLower.includes('zero') || titleLower.includes('empty');
    if (strat.includes('cpa'))
      b.push({ label: 'Aff CPA',  variant: 'warning'  });
    else if (isZero)
      b.push({ label: 'Aff Zero', variant: 'positive' });
    else
      b.push({ label: 'Aff Rev',  variant: 'warning'  });
  }

  return b;
}

function Badge({ label, variant, tooltip }) {
  const s = BADGE_STYLES[variant] || BADGE_STYLES.muted;
  const chip = (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold h-5 whitespace-nowrap" style={{ background: s.bg, color: s.color }}>
      {label}
    </span>
  );
  if (!tooltip) return chip;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent className="max-w-[220px] text-center">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

// ─── shared primitives ────────────────────────────────────────────────────────

const TRAJ_COLORS = { up: '#00BD71', down: '#E21351', flat: '#7a8499', inactive: '#4a5568' };
const TRAJ_RING  = { up: '#00BD71', down: '#E21351', flat: '#d4a843', inactive: '#6a7fa8' };
const TRAJ_DESC  = {
  up:       'Trending up — 7d turnover > 120% of weekly average',
  down:     'Trending down — 7d turnover < 80% of weekly average',
  flat:     'Stable — activity within normal range',
  inactive: 'Inactive — no turnover in the last 30 days',
};

function PlayerAvatar({ initials, trajectory, size = 'md' }) {
  const fill = TRAJ_COLORS[trajectory] || TRAJ_COLORS.flat;
  const ring = TRAJ_RING[trajectory]  || TRAJ_RING.flat;
  const dim  = size === 'lg' ? 56 : size === 'sm' ? 28 : 36;
  const font = size === 'lg' ? 18 : size === 'sm' ? 10 : 12;
  const avatar = (
    <div className="rounded-full flex items-center justify-center shrink-0 font-bold" style={{ width: dim, height: dim, fontSize: font, background: fill + '22', color: fill, border: `2px solid ${ring}88` }}>
      {initials}
    </div>
  );
  if (size !== 'md') return avatar;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatar}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-[200px]">
        <p className="font-semibold mb-1">Border color = activity trend</p>
        <div className="flex flex-col gap-0.5 text-[11px]">
          <span><span style={{ color: '#00BD71' }}>●</span> Green — trending up</span>
          <span><span style={{ color: '#E21351' }}>●</span> Red — trending down</span>
          <span><span style={{ color: '#d4a843' }}>●</span> Gold — stable</span>
          <span><span style={{ color: '#6a7fa8' }}>●</span> Blue-gray — inactive (30d)</span>
        </div>
        {trajectory && <p className="mt-1.5 text-ink-dim text-[10px]">{TRAJ_DESC[trajectory]}</p>}
      </TooltipContent>
    </Tooltip>
  );
}

function WLDots({ wl6 }) {
  return (
    <div className="flex gap-1.5 items-center">
      {[...(wl6 || [])].reverse().map((h, i, arr) => {
        const isNow = i === arr.length - 1;
        const color = h === null ? '#5c6271' : h ? '#00BD71' : '#E21351';
        const weekLabel = isNow ? 'current week' : `${arr.length - 1 - i}w ago`;
        const resultLabel = h === null ? 'no data' : h ? 'house lost (player won)' : 'house won (player lost)';
        return (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <div className="w-2 h-2 rounded-full shrink-0 cursor-default" style={{ background: color, outline: isNow ? `2px solid ${color}` : 'none', outlineOffset: '1px' }} />
            </TooltipTrigger>
            <TooltipContent>
              <p>{weekLabel} — {resultLabel}</p>
              <p className="text-[10px] text-ink-dim mt-0.5">Green = player won · Red = player lost · Gray = no data</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

// ─── Prediction chips ─────────────────────────────────────────────────────────

const CLTV_COLOR = { 0: '#7a8499', 1: '#E21351', 2: '#f0a500', 3: '#d4a843', 4: '#00BD71' };
const CLTV_LABEL = { 0: '≤ €10', 1: '€10–50', 2: '€50–200', 3: '€200–700', 4: '> €700' };

function pobaColor(poba) {
  if (poba < 0.3) return '#E21351';
  if (poba < 0.5) return '#FFD22F';
  return '#00BD71';
}

function PobaChip({ poba, poba30avg }) {
  const color = pobaColor(poba);
  const trend = poba30avg != null ? poba - poba30avg : null;
  const trendStr = trend != null ? (trend > 0 ? ` ↑` : trend < 0 ? ` ↓` : '') : '';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-default">
          <span className="text-[9px] text-ink-dim uppercase tracking-wide">PoBA</span>
          <span className="text-[10px] font-bold" style={{ color }}>
            {(poba * 100).toFixed(0)}%{trendStr}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        PoBA: {(poba * 100).toFixed(1)}%{poba30avg != null ? ` · 30d avg: ${(poba30avg * 100).toFixed(1)}%` : ''}
      </TooltipContent>
    </Tooltip>
  );
}

function CltvChip({ cltv }) {
  const color = CLTV_COLOR[cltv] || '#7a8499';
  const label = CLTV_LABEL[cltv] || '?';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-default">
          <span className="text-[9px] text-ink-dim uppercase tracking-wide">LTV</span>
          <span className="text-[10px] font-bold" style={{ color }}>L{cltv}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>CLTV bucket: L{cltv} — {label}</TooltipContent>
    </Tooltip>
  );
}

// ─── Card grid ────────────────────────────────────────────────────────────────

function PlayerCardItem({ player: p, onClick, pobaDelta }) {
  const badges = getBadges(p, pobaDelta);
  const ngrColor   = p.ngr_7d   >= 0 ? '#00BD71' : '#E21351';
  const lifeColor  = p.life_ngr >= 0 ? '#d4a843' : '#E21351';
  const bonusColor = p.bonusPct > 5 ? '#FFD22F' : p.bonusPct > 0 ? '#f4f1ec' : '#9aa0ad';
  return (
    <div onClick={onClick} className="rounded-lg border border-white/10 bg-surface-1 hover:bg-surface-2 transition-colors cursor-pointer flex flex-col">
      <div className="p-3 flex items-center gap-2.5">
        <PlayerAvatar initials={p.initials} trajectory={p.trajectory} />
        <div className="min-w-0">
          <p className="text-xs font-bold text-ink truncate">{p.email}</p>
          <p className="text-[10px] text-ink-dim">ID {p.user_id}</p>
        </div>
      </div>
      <div className="px-3 pb-2 flex flex-wrap gap-1 content-start h-[52px] overflow-hidden">
        {badges.map((b, i) => <Badge key={i} {...b} />)}
      </div>
      <div className="grid grid-cols-2 gap-px bg-white/5 border-t border-white/5 flex-1">
        {[
          { label: 'Lifetime NGR', value: fmtEur(p.life_ngr),      color: lifeColor },
          { label: '7d NGR',       value: fmtEur(p.ngr_7d),        color: ngrColor },
          { label: 'Turnover',     value: fmtEur(p.turnover_7d) },
          { label: 'Bonus %',      value: p.bonusPct.toFixed(1) + '%', color: bonusColor },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-surface-1 px-3 py-2">
            <p className="text-[9px] text-ink-dim uppercase tracking-wide mb-0.5">{label}</p>
            <p className="text-xs font-bold" style={{ color: color || '#f4f1ec' }}>{value}</p>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 flex items-center border-t border-white/8">
        <div className="flex-1 flex justify-center">
          <WLDots wl6={p.wl6} />
        </div>
        <span className="text-[10px] text-ink-dim">{p.playtime}</span>
      </div>
      <div className={`px-3 pb-2 pt-2 flex items-center gap-3 ${(p.poba != null || p.cltv != null) ? 'border-t border-white/5' : 'invisible'}`}>
        {p.poba != null
          ? <PobaChip poba={p.poba} poba30avg={p.poba30avg} />
          : <span className="inline-block text-[11px]">&nbsp;</span>}
        {p.cltv != null && <CltvChip cltv={p.cltv} />}
      </div>
    </div>
  );
}

// ─── Full table (column picker) ───────────────────────────────────────────────

const STORAGE_KEY = 'vip_dashboard_visible_cols';

function makeCols(period, sortDim = '7d') {
  const s = period;
  const dN = (base) => `${base}_${s}`;
  const dS = (base) => `${base}_${sortDim}`;
  const dim = { color: '#9aa0ad' };
  const dual = (v1, v2) => <span>{v1}<span style={dim}> / {v2}</span></span>;
  const dualEur = (p, d1, dn) => dual(fmtEur(p[d1]), fmtEur(p[dN(dn)]));
  const dualPct = (p, d1, dn) => dual(fmtPct(p[d1]), fmtPct(p[dN(dn)]));
  return [
    { cat: 'Money',    key: 'dep_7d',            label: 'Deposits',     defaultOn: true,  sortKey: dS('dep'),             desc: 'Total deposits.',                      render: p => dualEur(p, 'dep_1d', 'dep') },
    { cat: 'Money',    key: 'cash_7d',            label: 'Cashouts',     defaultOn: true,  sortKey: dS('cash'),            desc: 'Total cashouts.',                      render: p => dualEur(p, 'cash_1d', 'cash') },
    { cat: 'Perf',     key: 'ngr_7d',             label: 'NGR',          defaultOn: true,  sortKey: dS('ngr'),             desc: 'Net Gaming Revenue.',                  render: p => <span style={colorEur(p.ngr_7d)}>{dualEur(p, 'ngr_1d', 'ngr')}</span> },
    { cat: 'Perf',     key: 'ggr_7d',             label: 'GGR',          defaultOn: true,  sortKey: dS('ggr'),             desc: 'Gross Gaming Revenue.',                render: p => <span style={colorEur(p.ggr_7d)}>{dualEur(p, 'ggr_1d', 'ggr')}</span> },
    { cat: 'Perf',     key: 'turnover_7d',        label: 'Turnover',     defaultOn: false, sortKey: dS('turnover'),        desc: 'Total amount wagered.',                render: p => dualEur(p, 'turnover_1d', 'turnover') },
    { cat: 'Bonus',    key: 'bonus_7d',           label: 'Bonus',        defaultOn: true,  sortKey: dS('bonus'),           desc: 'Bonuses issued.',                      render: p => dualEur(p, 'bonus_1d', 'bonus') },
    { cat: 'Activity', key: 'playtime_7d_sec',    label: 'Playtime',     defaultOn: true,  sortKey: `playtime_${sortDim}_sec`, desc: 'Active session time.',            render: p => dual(fmtTime(p.playtime_1d_sec), fmtTime(p[`playtime_${s}_sec`])) },
    { cat: 'History',  key: 'wl6',               label: '6W W/L',       defaultOn: true,  sortKey: null,                  desc: 'Last 6 weeks of casino hold.',         render: p => <WLDots wl6={p.wl6} /> },
    { cat: 'History',  key: 'life_ngr',           label: 'Lifetime NGR', defaultOn: true,  sortKey: 'life_ngr',            desc: 'Total NGR since registration.',        render: p => <span style={colorEur(p.life_ngr, '#d4a843')}>{fmtEur(p.life_ngr)}</span> },
    { cat: 'Ratios',   key: 'avg_bet_size_7d',    label: 'Avg Bet',      defaultOn: false, sortKey: dS('avg_bet_size'),    desc: 'Average bet size = Turnover ÷ Bets.',  render: p => dualEur(p, 'avg_bet_size_1d', 'avg_bet_size') },
    { cat: 'Ratios',   key: 'house_edge_pct_7d',  label: 'House Edge',   defaultOn: false, sortKey: dS('house_edge_pct'), desc: 'Effective house edge.',                render: p => dualPct(p, 'house_edge_pct_1d', 'house_edge_pct') },
    { cat: 'Ratios',   key: 'ngr_dep_ratio_7d',   label: 'NGR/Dep',      defaultOn: false, sortKey: dS('ngr_dep_ratio'),  desc: 'NGR as % of deposits.',                render: p => <span style={{ color: (p.ngr_dep_ratio_7d ?? 0) >= 0 ? '#00BD71' : '#E21351' }}>{dualPct(p, 'ngr_dep_ratio_1d', 'ngr_dep_ratio')}</span> },
    { cat: 'Ratios',   key: 'bonus_ggr_ratio_7d', label: 'Bonus/GGR',    defaultOn: false, sortKey: dS('bonus_ggr_ratio'), desc: 'Bonus/GGR ratio.',                    render: p => <span style={{ color: p.bonus_ggr_ratio_7d > 80 ? '#FFD22F' : undefined }}>{dualPct(p, 'bonus_ggr_ratio_1d', 'bonus_ggr_ratio')}</span> },
    { cat: 'Other',    key: 'pay_systems',        label: 'Pay Methods',  defaultOn: false, sortKey: null,                  desc: 'Deposit payment methods.',             render: p => <span className="text-[11px] text-ink">{p.pay_systems || '—'}</span> },
    { cat: 'Predict',  key: 'poba',               label: 'PoBA',         defaultOn: false, sortKey: 'poba',                desc: 'Probability of Being Alive. Higher = less likely to churn.', render: p => p.poba != null ? <span style={{ color: pobaColor(p.poba) }} className="font-bold text-xs">{(p.poba * 100).toFixed(0)}%</span> : <span className="text-ink-dim">—</span> },
    { cat: 'Predict',  key: 'cltv',               label: 'CLTV',         defaultOn: false, sortKey: 'cltv',                desc: 'Customer Lifetime Value bucket (ML model). L0=Lowest … L4=High.',                         render: p => p.cltv != null ? <span style={{ color: CLTV_COLOR[p.cltv] || '#7a8499' }} className="font-bold text-xs">L{p.cltv} · {CLTV_LABEL[p.cltv]}</span> : <span className="text-ink-dim">—</span> },
  ];
}

function loadVisibleKeys(allCols) {
  try { const s = localStorage.getItem(STORAGE_KEY); if (s) return new Set(JSON.parse(s)); } catch {}
  return new Set(allCols.filter(c => c.defaultOn).map(c => c.key));
}

function ColumnPicker({ visibleKeys, onChange, allCols }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const toggle = (key) => {
    const next = new Set(visibleKeys);
    next.has(key) ? next.delete(key) : next.add(key);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...next])); } catch {}
    onChange(next);
  };
  const groups = [];
  const bycat = {};
  for (const col of allCols) {
    if (!bycat[col.cat]) { bycat[col.cat] = []; groups.push({ cat: col.cat, cols: bycat[col.cat] }); }
    bycat[col.cat].push(col);
  }
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} className={`flex items-center gap-1 px-2 py-1 text-[11px] cursor-pointer border rounded transition-colors ${open ? 'border-brand-yellow text-brand-yellow bg-brand-yellow/10' : 'border-white/10 text-ink-dim hover:border-brand-yellow hover:text-brand-yellow'}`}>
        ⊞ Columns
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 bg-surface-1 border border-white/10 rounded-lg min-w-[380px] py-0.5 shadow-2xl grid grid-cols-2">
          {groups.map(({ cat, cols }) => (
            <div key={cat}>
              <div className="text-[10px] text-ink-dim px-3 py-1 uppercase tracking-wider border-b border-white/8">{cat}</div>
              {cols.map(col => (
                <div key={col.key} onClick={() => toggle(col.key)} className="flex items-center gap-1 px-3 py-0.5 cursor-pointer hover:bg-surface-2">
                  <input type="checkbox" checked={visibleKeys.has(col.key)} readOnly className="accent-brand-yellow" />
                  <span className="text-xs text-ink">{col.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FullTable({ players, onSelect, cols, sortCol, sortDir, onSort }) {
  const { backofficeLinks } = useVipSource();
  const TH = ({ label, sortKey, description }) => (
    <th onClick={() => sortKey && onSort(sortKey)} style={{ padding: '8px 10px', fontSize: 10, color: '#9aa0ad', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: '1px solid rgba(31,36,48,1)', cursor: sortKey ? 'pointer' : 'default', userSelect: 'none', background: '#13161b' }}>
      <span className="inline-flex items-center gap-1">
        <span>{label}{sortKey && sortCol === sortKey ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</span>
        {description && <Tooltip><TooltipTrigger asChild><span className="inline-flex cursor-help" style={{ color: '#5c6271', lineHeight: 0 }} onClick={e => e.stopPropagation()}><Info className="h-3 w-3" /></span></TooltipTrigger><TooltipContent>{description}</TooltipContent></Tooltip>}
      </span>
    </th>
  );
  const td = { padding: '7px 10px', fontSize: 12, borderBottom: '1px solid rgba(31,36,48,1)', whiteSpace: 'nowrap' };
  return (
    <div className="overflow-x-auto">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH label="Player" sortKey="email" />
            {cols.map(c => <TH key={c.key} label={c.label} sortKey={c.sortKey} description={c.desc} />)}
          </tr>
        </thead>
        <tbody>
          {players.length === 0 && (
            <tr><td colSpan={cols.length + 1} style={{ padding: '20px 10px', fontSize: 12, color: '#9aa0ad', textAlign: 'center', borderBottom: '1px solid rgba(31,36,48,1)' }}>No players match the current filters.</td></tr>
          )}
          {players.map(p => (
            <tr key={p.user_id} onClick={() => onSelect(p.user_id, p)} className="cursor-pointer transition-colors hover:bg-surface-2/50">
              <td style={{ ...td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={p.days_until_birthday != null && p.days_until_birthday < 7 ? { fontWeight: 700, color: '#00e5ff' } : {}}>{p.email}</span>
                {backofficeLinks && p.user_id && <a href={`https://backoffice.bitstarz-kute.com/backend/players/${p.user_id}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="ml-1.5 text-ink-dim text-[10px] no-underline opacity-60 align-middle" title="Open in backoffice">↗</a>}
              </td>
              {cols.map(c => <td key={c.key} style={td}>{c.render(p)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── KpiStrip ─────────────────────────────────────────────────────────────────

function KpiStrip({ summary }) {
  if (!summary) return null;
  const cols = [
    {
      label: 'Deposits',
      yday: fmtEur(summary.dep_1d),
      week: fmtEur(summary.dep_7d),
      month: fmtEur(summary.dep_30d),
      color: '#00BD71',
    },
    {
      label: 'Cashouts',
      yday: fmtEur(summary.cash_1d),
      week: fmtEur(summary.cash_7d),
      month: fmtEur(summary.cash_30d),
      color: '#E21351',
    },
    {
      label: 'NGR',
      yday: fmtEur(summary.ngr_1d),
      week: fmtEur(summary.ngr_7d),
      month: fmtEur(summary.ngr_30d),
      color: (summary.ngr_7d ?? 0) >= 0 ? '#00BD71' : '#E21351',
      ydayColor: (summary.ngr_1d ?? 0) >= 0 ? '#00BD71' : '#E21351',
      monthColor: (summary.ngr_30d ?? 0) >= 0 ? '#00BD71' : '#E21351',
    },
    {
      label: 'GGR',
      yday: fmtEur(summary.ggr_1d),
      week: fmtEur(summary.ggr_7d),
      month: fmtEur(summary.ggr_30d),
      color: (summary.ggr_7d ?? 0) >= 0 ? '#00BD71' : '#E21351',
      ydayColor: (summary.ggr_1d ?? 0) >= 0 ? '#00BD71' : '#E21351',
      monthColor: (summary.ggr_30d ?? 0) >= 0 ? '#00BD71' : '#E21351',
    },
    {
      label: 'Lifetime NGR',
      weekLabel: 'total',
      yday: null,
      week: fmtEur(summary.life_ngr),
      month: null,
      color: '#d4a843',
    },
    {
      label: 'Active',
      yday: summary.active_1d ?? '—',
      week: summary.active_7d ?? '—',
      month: summary.active_30d ?? '—',
      color: '#f4f1ec',
    },
  ];
  return (
    <div className="grid grid-cols-6 border border-white/10 rounded-lg mb-5 overflow-hidden">
      {cols.map((k, i) => (
        <div key={i} className={`px-4 py-3 bg-surface-1 ${i < 5 ? 'border-r border-white/10' : ''}`}>
          <p className="text-[10px] text-ink-dim uppercase tracking-wider mb-2">{k.label}</p>
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[9px] text-ink-dim">{k.weekLabel ?? '7d'}</span>
              <span className="text-lg font-bold" style={{ color: k.color }}>{k.week}</span>
            </div>
            {k.yday != null && (
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[9px] text-ink-dim">yday</span>
                <span className="text-sm font-semibold" style={{ color: k.ydayColor || k.color, opacity: 0.75 }}>{k.yday}</span>
              </div>
            )}
            {k.month != null && (
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[9px] text-ink-dim">30d</span>
                <span className="text-sm font-semibold" style={{ color: k.monthColor || k.color, opacity: 0.6 }}>{k.month}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Upcoming birthdays ───────────────────────────────────────────────────────

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DOW_ABBR   = ['SUN','MON','TUE','WED','THU','FRI','SAT'];

function fmtBirthdayDate(dob) {
  const d = new Date(dob);
  if (isNaN(d)) return '';
  return `${MONTH_ABBR[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
}

function daysUntilLabel(n) {
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  return `in ${n} days`;
}

const FAR_BIRTHDAY_ACCENT = '#a78bfa'; // beyond the 7-day window — distinct from today's green / this-week's gold

function BirthdayRow({ player: p, onSelect, accent }) {
  return (
    <div onClick={() => onSelect(p.user_id, p)}
      style={accent ? { borderColor: `${accent}40`, background: `${accent}0f` } : undefined}
      className={`inline-flex items-center gap-2 rounded-lg border transition-colors cursor-pointer px-2 py-1.5 min-w-0 ${accent ? 'hover:brightness-125' : 'border-white/8 bg-surface-2/40 hover:bg-surface-2'}`}>
      <PlayerAvatar initials={p.initials} trajectory={p.trajectory} size="sm" />
      <div className="min-w-0">
        <p className="text-[11px] font-bold text-ink truncate">{p.username || p.email}</p>
        <p className={`text-[9px] truncate ${accent ? '' : 'text-ink-dim'}`} style={accent ? { color: accent } : undefined}>
          {fmtBirthdayDate(p.date_of_birth)} · {daysUntilLabel(p.days_until_birthday)}
        </p>
      </div>
    </div>
  );
}

function BirthdaysPanel({ players, onSelect }) {
  const [showAll, setShowAll] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null); // 0-6 offset from today, or null

  const next7Buckets = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      return {
        dow: DOW_ABBR[day.getDay()],
        dom: day.getDate(),
        count: players.filter(p => p.days_until_birthday === i).length,
        isToday: i === 0,
      };
    });
  }, [players]);

  const upcoming7d = useMemo(() => (
    players.filter(p => p.days_until_birthday != null && p.days_until_birthday <= 6)
  ), [players]);

  const todayPlayers = useMemo(() => (
    upcoming7d.filter(p => p.days_until_birthday === 0).sort((a, b) => (b.life_ngr || 0) - (a.life_ngr || 0))
  ), [upcoming7d]);

  const allRanked = useMemo(() => (
    [...upcoming7d].sort((a, b) => (a.days_until_birthday - b.days_until_birthday) || ((b.life_ngr || 0) - (a.life_ngr || 0)))
  ), [upcoming7d]);

  const todayCount = next7Buckets[0]?.count || 0;
  const next7Count = next7Buckets.reduce((s, d) => s + d.count, 0);

  const nextUpcoming = useMemo(() => (
    players
      .filter(p => p.days_until_birthday != null)
      .sort((a, b) => a.days_until_birthday - b.days_until_birthday)
      .slice(0, 5)
  ), [players]);

  const dayPlayers = useMemo(() => (
    selectedDay == null ? [] : upcoming7d.filter(p => p.days_until_birthday === selectedDay).sort((a, b) => (b.life_ngr || 0) - (a.life_ngr || 0))
  ), [upcoming7d, selectedDay]);

  const handleDayClick = (i) => {
    if (next7Buckets[i].count === 0) return;
    setShowAll(false);
    setSelectedDay(prev => prev === i ? null : i);
  };

  const listPlayers = selectedDay != null ? dayPlayers
    : showAll || todayPlayers.length === 0 ? allRanked
    : todayPlayers;
  const listHeading = selectedDay != null ? `${next7Buckets[selectedDay].dow} ${next7Buckets[selectedDay].dom}`
    : showAll || todayPlayers.length === 0 ? 'Upcoming'
    : 'Today';
  const sortSuffix = listHeading === 'Upcoming' ? 'By date, then lifetime NGR' : 'Ranked by lifetime NGR';
  const maxCount = Math.max(1, ...next7Buckets.map(d => d.count));

  return (
    <div className="border border-white/10 rounded-lg mb-5 bg-surface-1 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-ink-dim uppercase tracking-wider">Upcoming Birthdays</span>
          <span className="text-sm font-bold" style={{ color: '#00BD71' }}>{todayCount}</span>
          <span className="text-[11px] text-ink-dim">today</span>
          <span className="text-white/10">|</span>
          <span className="text-sm font-bold text-ink">{next7Count}</span>
          <span className="text-[11px] text-ink-dim">next 7 days</span>
        </div>
        {next7Count > 0 && (selectedDay != null ? (
          <button type="button" onClick={() => setSelectedDay(null)}
            className="text-[11px] font-bold px-3 py-1 rounded-full border border-white/10 text-ink-dim hover:border-white/20 hover:text-ink transition-colors">
            Clear filter
          </button>
        ) : (
          <>
            {!showAll && listPlayers.length < upcoming7d.length && (
              <button type="button" onClick={() => setShowAll(true)}
                className="text-[11px] font-bold px-3 py-1 rounded-full border border-white/10 text-ink-dim hover:border-white/20 hover:text-ink transition-colors">
                Open all {upcoming7d.length} →
              </button>
            )}
            {showAll && todayPlayers.length > 0 && (
              <button type="button" onClick={() => setShowAll(false)}
                className="text-[11px] font-bold px-3 py-1 rounded-full border border-white/10 text-ink-dim hover:border-white/20 hover:text-ink transition-colors">
                Show today
              </button>
            )}
          </>
        ))}
      </div>

      <div className="flex flex-wrap gap-4 p-4">
        <div className="flex-1 min-w-[240px]">
          {next7Count === 0 ? (
            <>
              <p className="text-xs text-ink-dim mb-3">No upcoming VIP birthdays in the next 7 days.</p>
              {nextUpcoming.length > 0 ? (
                <>
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: FAR_BIRTHDAY_ACCENT }}>Next up</p>
                  <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                    {nextUpcoming.map(p => <BirthdayRow key={p.user_id} player={p} onSelect={onSelect} accent={FAR_BIRTHDAY_ACCENT} />)}
                  </div>
                </>
              ) : (
                <p className="text-[11px] text-ink-mute">No birth dates on file for these VIPs.</p>
              )}
            </>
          ) : (
            <>
              <p className="text-[10px] text-ink-dim uppercase tracking-wider mb-2">{listHeading} · {sortSuffix}</p>
              <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                {listPlayers.map(p => <BirthdayRow key={p.user_id} player={p} onSelect={onSelect} />)}
              </div>
            </>
          )}
        </div>

        <div className="w-[220px] shrink-0">
          <p className="text-[10px] text-ink-dim uppercase tracking-wider mb-2">Next 7 days</p>
          <div className="flex items-end gap-1.5 h-[70px]">
            {next7Buckets.map((d, i) => {
              const clickable = d.count > 0;
              const selected  = selectedDay === i;
              return (
                <div key={i} onClick={() => handleDayClick(i)}
                  className={`flex flex-col items-center gap-1 flex-1 rounded px-0.5 py-0.5 transition-colors ${clickable ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'} ${selected ? 'bg-white/10' : ''}`}>
                  <span className="text-[10px] font-bold" style={{ color: d.isToday ? '#00BD71' : d.count > 0 ? '#d4a843' : '#5c6271' }}>
                    {d.count > 0 ? d.count : ''}
                  </span>
                  <div className="w-full rounded" style={{
                    height: Math.max(6, (d.count / maxCount) * 40),
                    background: d.isToday ? '#00BD71' : d.count > 0 ? '#d4a84388' : 'rgba(255,255,255,0.06)',
                    outline: selected ? '2px solid #f4f1ec' : 'none',
                    outlineOffset: 1,
                  }} />
                  <span className="text-[9px] text-ink-dim uppercase">{d.dow}</span>
                  <span className="text-[9px] text-ink-dim">{d.dom}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Toolbar constants ────────────────────────────────────────────────────────

const POBA_FILTERS = [
  { key: 'poba_healthy',  label: 'Healthy'  },
  { key: 'poba_at_risk',  label: 'At risk'  },
  { key: 'poba_alarming', label: 'Alarming' },
  { key: 'poba_up',       label: 'Trend up'   },
  { key: 'poba_down',     label: 'Trend down' },
];

const COMP_SIGNAL_FILTERS = [
  { key: 'comp_burning',   label: 'Burning'   },
  { key: 'comp_efficient', label: 'Efficient' },
  { key: 'comp_ngr_down',  label: 'NGR down'  },
  { key: 'comp_ok',        label: 'OK'        },
];

const AFF_FILTERS = [
  { key: 'aff_rev',  label: 'Aff Rev'  },
  { key: 'aff_zero', label: 'Aff Zero' },
  { key: 'aff_cpa',  label: 'Aff CPA'  },
  { key: 'aff_none', label: 'Not Affiliated' },
];


function FilterDropdown({ label, filters, value, onSelect, players, pobaDelta, extra }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const activeChild = filters.find(f => f.key === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors inline-flex items-center gap-1 ${
          activeChild
            ? 'border-cta text-cta bg-cta/10'
            : 'border-white/10 text-ink-dim hover:border-white/20 hover:text-ink'
        }`}
      >
        {activeChild ? `${label}: ${activeChild.label}` : label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-50 min-w-[170px] rounded-lg border border-white/10 bg-surface-1 shadow-xl overflow-hidden">
          {extra && (
            <div className="px-3 py-2 border-b border-white/10">
              {extra}
            </div>
          )}
          <div className="p-1">
            {filters.map(f => {
              const count = applyFilter(players, f.key, pobaDelta / 100).length;
              const isSelected = value === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => { onSelect(isSelected ? 'all' : f.key); setOpen(false); }}
                  className={`w-full text-left px-2 py-1.5 text-xs rounded flex items-center justify-between transition-colors ${
                    isSelected ? 'text-cta bg-cta/10' : 'text-ink-dim hover:text-ink hover:bg-surface-2'
                  }`}
                >
                  {f.label}
                  <span className="text-[10px] text-ink-mute ml-3">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


const SORTS = [
  { key: 'life_ngr',    label: 'Lifetime NGR' },
  { key: 'ngr_7d',     label: '7d NGR' },
  { key: 'ngr_30d',    label: '30d NGR' },
  { key: 'turnover_7d', label: '7d Turnover' },
  { key: 'dep_7d',     label: 'Deposits 7d' },
  { key: 'bonusPct',   label: 'Bonus %' },
];

// ─── DashboardView ────────────────────────────────────────────────────────────

export default function DashboardView({ rawData, agent }) {
  const navigate = useNavigate();
  const { hidden, paths } = useVipSource();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pobaFilter, setPobaFilter] = useState('all');
  const [compFilter, setCompFilter] = useState('all');
  const [affFilter,  setAffFilter]  = useState('all');
  const [pobaDelta, setPobaDelta] = useState(3); // pp units, converted to fraction on use
  const [sort, setSort] = useState('ngr_7d');
  const [sortDir, setSortDir] = useState('desc');
  // Restores the toggle a player was opened from when navigating back via PlayerProfilePage's back button.
  const [view, setView] = useState(() => (searchParams.get('view') === 'table' ? 'table' : 'cards'));

  // Keep the URL in sync so the browser's native Back button (not just our in-app back
  // link) also restores the toggle that was active when a player was opened.
  useEffect(() => {
    setSearchParams((current) => {
      if (current.get('view') === view) return current;
      const next = new URLSearchParams(current);
      next.set('view', view);
      return next;
    }, { replace: true });
  }, [view, setSearchParams]);
  const [period, setPeriod] = useState('7d');
  const [sortDim, setSortDim] = useState('7d');
  const [tableSort, setTableSort] = useState('ngr_7d');
  const [tableSortDir, setTableSortDir] = useState('desc');
  const [visibleKeys, setVisibleKeys] = useState(() => loadVisibleKeys(makeCols('7d')));

  const allCols    = useMemo(
    () => makeCols(period, sortDim).filter(c => !(hidden.poba && c.cat === 'Predict')),
    [period, sortDim, hidden.poba],
  );
  const activeCols = useMemo(() => allCols.filter(c => visibleKeys.has(c.key)), [allCols, visibleKeys]);

  const players = useMemo(() => {
    const rows = Array.isArray(rawData) ? rawData : (rawData?.data || []);
    const enriched = rows.map(enrichPlayer);
    // A source without comp signals shows no comp badges or filter counts.
    return hidden.comps ? enriched.map(p => ({ ...p, comp_signal: null })) : enriched;
  }, [rawData, hidden.comps]);

  // Per-group bases for accurate dropdown counts (apply every OTHER group's filter)
  const pobaBase = useMemo(() => {
    let r = players;
    if (compFilter !== 'all') r = applyFilter(r, compFilter, pobaDelta / 100);
    if (affFilter  !== 'all') r = applyFilter(r, affFilter,  pobaDelta / 100);
    return r;
  }, [players, compFilter, affFilter, pobaDelta]);
  const compBase = useMemo(() => {
    let r = players;
    if (pobaFilter !== 'all') r = applyFilter(r, pobaFilter, pobaDelta / 100);
    if (affFilter  !== 'all') r = applyFilter(r, affFilter,  pobaDelta / 100);
    return r;
  }, [players, pobaFilter, affFilter, pobaDelta]);
  const affBase = useMemo(() => {
    let r = players;
    if (pobaFilter !== 'all') r = applyFilter(r, pobaFilter, pobaDelta / 100);
    if (compFilter !== 'all') r = applyFilter(r, compFilter, pobaDelta / 100);
    return r;
  }, [players, pobaFilter, compFilter, pobaDelta]);

  const filtered = useMemo(() => {
    let r = players;
    if (pobaFilter !== 'all') r = applyFilter(r, pobaFilter, pobaDelta / 100);
    if (compFilter !== 'all') r = applyFilter(r, compFilter, pobaDelta / 100);
    if (affFilter  !== 'all') r = applyFilter(r, affFilter,  pobaDelta / 100);
    return r;
  }, [players, pobaFilter, compFilter, affFilter, pobaDelta]);

  const sorted = useMemo(() => {
    if (view === 'table') {
      return [...filtered].sort((a, b) => {
        const av = a[tableSort] ?? 0, bv = b[tableSort] ?? 0;
        return tableSortDir === 'asc' ? av - bv : bv - av;
      });
    }
    return applySort(filtered, sort, sortDir);
  }, [filtered, view, sort, sortDir, tableSort, tableSortDir]);

  const handleTableSort = (col) => {
    if (col === tableSort) setTableSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTableSort(col); setTableSortDir('desc'); }
  };

  const handleSortDimChange = (dim) => {
    setSortDim(dim);
    setTableSort(prev => prev.replace(/_1d(?=_|$)|_7d(?=_|$)|_30d(?=_|$)/, `_${dim}`));
  };

  const goToPlayer = (userId, player) => navigate(`${paths.player}/${userId}?from=dashboard&view=${view}`, { state: { player } });

  if (!rawData) return <p className="text-xs text-ink-dim p-4">No data</p>;

  return (
    <div>
      <KpiStrip summary={rawData?.summary} />
      {!hidden.birthdays && <BirthdaysPanel players={players} onSelect={goToPlayer} />}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* All */}
          <button type="button" onClick={() => { setPobaFilter('all'); setCompFilter('all'); setAffFilter('all'); }}
            className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${pobaFilter === 'all' && compFilter === 'all' && affFilter === 'all' ? 'border-cta text-cta bg-cta/10' : 'border-white/10 text-ink-dim hover:border-white/20 hover:text-ink'}`}>
            All
          </button>

          {/* PoBA group */}
          {!hidden.poba && <FilterDropdown
            label="PoBA"
            filters={POBA_FILTERS}
            value={pobaFilter}
            onSelect={setPobaFilter}
            players={pobaBase}
            pobaDelta={pobaDelta}
            extra={
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[10px] text-ink-dim flex items-center gap-1 cursor-default">
                    Trend Δ≥
                    <input
                      type="number"
                      min={0} max={50} step={1}
                      value={pobaDelta}
                      onChange={e => setPobaDelta(Math.max(0, Math.min(50, Number(e.target.value))))}
                      className="w-9 text-center bg-surface-2 border border-white/15 rounded px-1 py-0.5 text-xs text-ink focus:outline-none focus:border-white/30 cursor-text"
                    />
                    pp
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Minimum change (pp = percentage points) required to flag Trend up/down.
                  Trend down: 7d min is at least this far below the 30d average.
                  Trend up: 7d average is at least this far above the 30d average.
                </TooltipContent>
              </Tooltip>
            }
          />}

          {/* Comp signal group */}
          {!hidden.comps && <FilterDropdown
            label="Comp"
            filters={COMP_SIGNAL_FILTERS}
            value={compFilter}
            onSelect={setCompFilter}
            players={compBase}
            pobaDelta={pobaDelta}
          />}

          {/* Affiliate group */}
          {!hidden.affiliates && <FilterDropdown
            label="Affiliate"
            filters={AFF_FILTERS}
            value={affFilter}
            onSelect={setAffFilter}
            players={affBase}
            pobaDelta={pobaDelta}
          />}

          <span className="text-[11px] text-ink-dim ml-1">{sorted.length} players</span>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {view === 'cards' && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-ink-dim uppercase tracking-wider">Sort</span>
              {SORTS.map(s => {
                const active = sort === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => {
                      if (active) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
                      else { setSort(s.key); setSortDir('desc'); }
                    }}
                    className={`px-2.5 py-1 text-[11px] rounded border transition-colors inline-flex items-center gap-1 ${active ? 'border-brand-yellow text-brand-yellow bg-brand-yellow/10' : 'border-white/10 text-ink-dim hover:border-white/20 hover:text-ink'}`}
                  >
                    {s.label}
                    {active && <span className="text-[10px] leading-none">{sortDir === 'desc' ? '↓' : '↑'}</span>}
                  </button>
                );
              })}
            </div>
          )}
          {view === 'table' && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-ink-dim uppercase tracking-wider">Period</span>
                <ToggleGroup value={period} onChange={v => v && setPeriod(v)}>
                  <ToggleItem value="7d">7d</ToggleItem>
                  <ToggleItem value="30d">30d</ToggleItem>
                </ToggleGroup>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-ink-dim uppercase tracking-wider">Sort dim</span>
                <ToggleGroup value={sortDim} onChange={v => v && handleSortDimChange(v)}>
                  <ToggleItem value="1d">1d</ToggleItem>
                  <ToggleItem value="7d">7d</ToggleItem>
                  <ToggleItem value="30d">30d</ToggleItem>
                </ToggleGroup>
              </div>
              <ColumnPicker visibleKeys={visibleKeys} onChange={setVisibleKeys} allCols={allCols} />
            </>
          )}
          <div className="flex items-center rounded-lg border border-white/10 overflow-hidden">
            {[
              { key: 'cards', Icon: LayoutGrid, label: 'Card view' },
              { key: 'table', Icon: Rows,       label: 'Table view' },
            ].map(({ key, Icon, label }) => (
              <button key={key} type="button" onClick={() => setView(key)} aria-label={label}
                className={`p-1.5 transition-colors ${view === key ? 'bg-surface-2 text-ink' : 'text-ink-dim hover:text-ink'}`}>
                <Icon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      {view === 'cards' ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
          {sorted.length === 0
            ? <p className="text-xs text-ink-dim col-span-full py-8 text-center">No players match the current filter.</p>
            : sorted.map(p => <PlayerCardItem key={p.user_id} player={p} onClick={() => goToPlayer(p.user_id, p)} pobaDelta={pobaDelta / 100} />)
          }
        </div>
      ) : (
        <FullTable
          players={sorted}
          onSelect={goToPlayer}
          cols={activeCols}
          sortCol={tableSort}
          sortDir={tableSortDir}
          onSort={handleTableSort}
        />
      )}
    </div>
  );
}
